import { useEffect, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { QRCodeCanvas } from "qrcode.react";
import { Copy, LogOut, Users, Calendar, CheckCircle2, Edit2, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { http } from "@/lib/api";
import { useVisitorAuth } from "@/context/VisitorAuthContext";

export default function MyAccountPage() {
    const { visitor, loading, logout } = useVisitorAuth();
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [fetching, setFetching] = useState(true);

    useEffect(() => {
        if (!visitor) return;
        (async () => {
            try {
                const r = await http.get("/visitor/account");
                setData(r.data);
            } catch (e) {
                toast.error("Could not load account");
            } finally {
                setFetching(false);
            }
        })();
    }, [visitor]);

    if (loading) return null;
    if (!visitor) return <Navigate to="/sign-in" replace />;

    const needsProfile = data?.lead?.needs_profile;
    const lead = data?.lead || {};
    const appt = data?.appointment;
    const refUrl = data?.referral_url;

    const copyRef = async () => {
        if (!refUrl) return;
        await navigator.clipboard.writeText(refUrl);
        toast.success("Referral link copied");
    };

    const handleLogout = async () => {
        await logout();
        navigate("/", { replace: true });
    };

    return (
        <div className="min-h-screen" data-testid="my-account-page">
            <Navbar />
            <section className="max-w-5xl mx-auto px-4 sm:px-6 py-10 space-y-6">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div className="flex items-center gap-3">
                        {visitor.picture && (
                            <img src={visitor.picture} alt="" className="w-12 h-12 rounded-full border border-white/10" />
                        )}
                        <div>
                            <div className="text-xs text-emerald-400 uppercase tracking-wider font-mono">Signed in</div>
                            <div className="text-xl font-bold text-white">{visitor.name || visitor.email}</div>
                            <div className="text-xs text-slate-400">{visitor.email}</div>
                        </div>
                    </div>
                    <button
                        data-testid="visitor-logout-btn"
                        onClick={handleLogout}
                        className="btn-ghost !py-2 !min-h-0 text-sm"
                    >
                        <LogOut className="w-4 h-4" /> Sign out
                    </button>
                </div>

                {fetching && (
                    <div className="card-surface p-10 text-center text-slate-400 text-sm">Loading your account…</div>
                )}

                {!fetching && needsProfile && (
                    <div className="card-surface p-5 border-amber-500/30 bg-amber-500/5" data-testid="profile-incomplete-banner">
                        <div className="flex items-start justify-between gap-3 flex-wrap">
                            <div>
                                <div className="text-sm font-bold text-amber-300">Finish your profile to see real options</div>
                                <div className="text-xs text-slate-400 mt-1">Takes 15 seconds — just your ZIP, your situation, and anything else you'd like us to shop for you.</div>
                            </div>
                            <Link to="/complete-profile" data-testid="complete-profile-link" className="btn-primary !py-2 !min-h-0 text-sm">
                                Find my options
                            </Link>
                        </div>
                    </div>
                )}

                {!fetching && !needsProfile && (
                    <div className="grid md:grid-cols-2 gap-6">
                        <StatusCard lead={lead} />
                        <AppointmentCard appt={appt} />
                    </div>
                )}

                {!fetching && refUrl && (
                    <div className="card-surface p-6" data-testid="referral-card">
                        <div className="flex items-start justify-between gap-6 flex-wrap">
                            <div className="flex-1 min-w-[240px]">
                                <div className="flex items-center gap-2">
                                    <Users className="w-4 h-4 text-emerald-400" />
                                    <h2 className="text-lg font-bold text-white">Your referral link</h2>
                                </div>
                                <p className="text-xs text-slate-400 mt-1">
                                    Share with friends — when they sign up through your link, your referrals count goes up.
                                </p>
                                <div className="mt-4 flex items-center gap-2 bg-black/30 border border-white/10 rounded-lg px-3 py-2.5">
                                    <code className="flex-1 text-xs text-emerald-300 truncate" data-testid="referral-url">{refUrl}</code>
                                    <button
                                        onClick={copyRef}
                                        data-testid="copy-referral-btn"
                                        className="shrink-0 text-slate-400 hover:text-white"
                                        aria-label="Copy link"
                                    >
                                        <Copy className="w-4 h-4" />
                                    </button>
                                </div>
                                <div className="mt-4 inline-flex items-center gap-2 bg-emerald-500/15 border border-emerald-500/30 rounded-lg px-3 py-1.5">
                                    <span className="text-xs text-emerald-300 font-mono">Referrals so far</span>
                                    <span className="text-sm font-bold text-white" data-testid="referrals-count">{lead.referrals_count || 0}</span>
                                </div>
                            </div>
                            <div className="shrink-0 bg-white rounded-xl p-3">
                                <QRCodeCanvas value={refUrl} size={120} level="M" />
                            </div>
                        </div>
                    </div>
                )}

                {!fetching && !needsProfile && (
                    <div className="card-surface p-5">
                        <div className="flex items-center justify-between gap-3">
                            <div>
                                <div className="text-sm font-bold text-white">Preferences</div>
                                <div className="text-xs text-slate-400 mt-1">
                                    ZIP {lead.zip_code || "—"} · {lead.customer_type || "—"} · {lead.service_interests?.length
                                        ? `Interested in: ${lead.service_interests.map((s) => s.replace("_", " ")).join(", ")}`
                                        : "Only electricity for now"}
                                </div>
                            </div>
                            <Link to="/complete-profile" className="btn-ghost !py-2 !min-h-0 text-sm">
                                <Edit2 className="w-4 h-4" /> Edit
                            </Link>
                        </div>
                    </div>
                )}
            </section>
            <Footer />
        </div>
    );
}

function StatusCard({ lead }) {
    const label = {
        new: "In the queue — Jeffrey will reach out shortly.",
        contacted: "Jeffrey reached out — check your preferred channel.",
        follow_up: "Awaiting your follow-up.",
        qualified: "Qualified — options being prepared.",
        completed: "You're all set — thanks for choosing PowerSaver TX!",
        signup_pending: "Profile incomplete.",
        archived: "Archived.",
    }[lead?.status] || "In progress.";
    return (
        <div className="card-surface p-5" data-testid="lead-status-card">
            <div className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                <h2 className="text-lg font-bold text-white">Your request</h2>
            </div>
            <div className="mt-3 text-sm text-slate-300">{label}</div>
            {lead?.priority && (
                <div className="mt-3 text-xs text-slate-500 font-mono uppercase tracking-wider">
                    priority: {lead.priority}
                </div>
            )}
        </div>
    );
}

function AppointmentCard({ appt }) {
    return (
        <div className="card-surface p-5" data-testid="appointment-card">
            <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-emerald-400" />
                <h2 className="text-lg font-bold text-white">Appointment</h2>
            </div>
            {appt ? (
                <>
                    <div className="mt-3 text-sm text-slate-200">
                        {new Date(appt.slot_iso).toLocaleString(undefined, {
                            weekday: "short", month: "short", day: "numeric",
                            hour: "numeric", minute: "2-digit",
                        })}
                    </div>
                    <div className="mt-1 text-xs text-slate-500 font-mono uppercase tracking-wider">
                        {appt.channel || "call"} · {appt.status}
                    </div>
                </>
            ) : (
                <div className="mt-3 text-sm text-slate-400">
                    No appointment on the books.{" "}
                    <Link to="/thank-you" className="text-emerald-400 underline inline-flex items-center gap-1">
                        Pick a time <ExternalLink className="w-3 h-3" />
                    </Link>
                </div>
            )}
        </div>
    );
}
